/**
 * Page 04 — our presence: the six sites by Age, the location allocation per Age and the per-age read-out. The map is
 * a bundled SVG: `JORDAN_OUTLINE` is Jordan's polygon (longitude, latitude) from the Natural Earth 1:110m world
 * atlas the board loaded from a CDN, decoded once to plain data — nothing is fetched at runtime.
 */
export type PresenceAge = 'BRONZE' | 'SILVER' | 'GOLDEN';
export type PresenceFilter = 'ALL' | PresenceAge;

export const PRESENCE_INTRO = {
  eyebrow: 'Network & coverage · Page 04',
  title: 'Our presence — where we are, and where we are looking',
  body: 'Each age adds points on the map: Amman first, greater Amman next, then north and south for national coverage.',
  figure: 'Fig. 4 — Network build-out',
} as const;

export const JORDAN_OUTLINE: readonly (readonly [number, number])[] = [
  [35.54, 32.39],
  [35.72, 32.71],
  [36.83, 32.31],
  [38.79, 33.38],
  [39.2, 32.16],
  [39.0, 32.01],
  [37.0, 31.51],
  [38.0, 30.51],
  [37.67, 30.34],
  [37.5, 30.0],
  [36.74, 29.87],
  [36.5, 29.51],
  [36.07, 29.2],
  [34.95, 29.36],
  [34.92, 29.5],
  [35.42, 31.1],
  [35.4, 31.49],
  [35.54, 31.78],
  [35.54, 32.39],
];

export interface Site {
  name: string;
  sub: string;
  lon: number;
  lat: number;
  age: PresenceAge;
  /** "We are here". */
  current?: boolean;
  /** Label offset from the point in SVG units (a leader line joins them when it is far), so the Amman cluster stays legible. */
  labelDx: number;
  labelDy: number;
}

export const SITES: readonly Site[] = [
  {
    name: 'Amman · Mecca St.',
    sub: 'Flagship showroom — we are here',
    lon: 35.85,
    lat: 31.97,
    age: 'BRONZE',
    current: true,
    labelDx: 40,
    labelDy: -62,
  },
  { name: 'Bayader', sub: 'Service + parts', lon: 35.83, lat: 31.92, age: 'BRONZE', labelDx: -46, labelDy: 46 },
  { name: 'Amman pop-up', sub: 'Showroom', lon: 35.91, lat: 31.98, age: 'SILVER', labelDx: 96, labelDy: -18 },
  { name: 'Marka / Zarqa', sub: 'Showroom + service', lon: 36.02, lat: 32.0, age: 'SILVER', labelDx: 70, labelDy: 30 },
  { name: 'Irbid', sub: 'Showroom + service center', lon: 35.85, lat: 32.55, age: 'GOLDEN', labelDx: -14, labelDy: -8 },
  { name: 'Aqaba', sub: 'Showroom + service center', lon: 35.0, lat: 29.53, age: 'GOLDEN', labelDx: 16, labelDy: 4 },
];

export const NEIGHBOURS: readonly { name: string; lon: number; lat: number }[] = [
  { name: 'SAUDI ARABIA', lon: 37.6, lat: 30.6 },
  { name: 'IRAQ', lon: 38.6, lat: 32.7 },
  { name: 'SYRIA', lon: 36.6, lat: 33.0 },
  { name: 'ISRAEL', lon: 34.75, lat: 30.9 },
];

export const AGE_FILTERS: readonly { key: PresenceFilter; label: string }[] = [
  { key: 'ALL', label: 'All ages' },
  { key: 'BRONZE', label: 'Bronze' },
  { key: 'SILVER', label: 'Silver' },
  { key: 'GOLDEN', label: 'Golden' },
];

export const AGE_LEGEND: Readonly<Record<PresenceAge, string>> = {
  BRONZE: 'Bronze Age · 2026',
  SILVER: 'Silver Age · 2027',
  GOLDEN: 'Golden Age · 2028',
};

export const AGE_COLUMNS: readonly { key: PresenceAge; label: string; sub: string }[] = [
  { key: 'BRONZE', label: 'Bronze · 2026', sub: 'Amman — we are here' },
  { key: 'SILVER', label: 'Silver · 2027', sub: 'Greater Amman' },
  { key: 'GOLDEN', label: 'Golden · 2028', sub: 'North & south' },
];

export interface AllocationCell {
  n: string;
  delta: string;
  what: string;
}

export interface AllocationRow {
  name: string;
  /** Bronze, Silver, Golden — cumulative. */
  cells: readonly [AllocationCell, AllocationCell, AllocationCell];
}

export const ALLOCATION_TITLE = 'Location allocation';

export const ALLOCATION: readonly AllocationRow[] = [
  {
    name: 'Showrooms',
    cells: [
      { n: '1', delta: '', what: 'Mecca St. flagship, 650+ sqm' },
      { n: '3', delta: '+2', what: 'Amman pop-up · Marka/Zarqa' },
      { n: '5', delta: '+2', what: 'Irbid · Aqaba' },
    ],
  },
  {
    name: 'Service centers',
    cells: [
      { n: '1', delta: '', what: 'Bayader' },
      { n: '2', delta: '+1', what: 'Marka/Zarqa · +4 partner workshops' },
      { n: '4', delta: '+2', what: 'Irbid · Aqaba · mobile unit' },
    ],
  },
  {
    name: 'Spare parts points',
    cells: [
      { n: '1', delta: '', what: 'Bayader counter + stock' },
      { n: '3', delta: '+2', what: 'Branch + sub-dealers' },
      { n: '5', delta: '+2', what: 'North & south sub-dealers' },
    ],
  },
  {
    name: 'Traders / dealers',
    cells: [
      { n: '—', delta: '', what: 'Network not yet activated' },
      { n: '7', delta: '+7', what: 'Trader network points' },
      { n: '10', delta: '+3', what: 'Nationwide coverage' },
    ],
  },
];

export const ALLOCATION_TOTALS: readonly [string, string, string] = ['3', '15', '24'];

export interface AgeView {
  headline: string;
  blurb: string;
  countsLabel: string;
  /** Which allocation column the counts read from. */
  column: 0 | 1 | 2;
  total: string;
}

export const AGE_VIEW: Readonly<Record<PresenceFilter, AgeView>> = {
  ALL: {
    headline: 'Three ages, 24 points on the map',
    blurb:
      'Every age keeps what the last one built: one showroom and one service point today, greater Amman next, then north and south for national coverage. Select an age to isolate it.',
    countsLabel: 'By the end of Golden · cumulative',
    column: 2,
    total: '24',
  },
  BRONZE: {
    headline: 'Bronze — Amman, we are here',
    blurb:
      'Flagship showroom on Mecca Street (650+ sqm), beside Mecca Mall and Plaza Outlets footfall, with service and spare parts at Bayader. Every 2026 sale, visit and part flows through these two points.',
    countsLabel: 'By the end of Bronze · cumulative',
    column: 0,
    total: '3',
  },
  SILVER: {
    headline: 'Silver — greater Amman',
    blurb:
      'Amman pop-up showroom plus a Marka/Zarqa showroom and service center. The trader network (7 points) and multi-brand service partners (4) extend reach without premises.',
    countsLabel: 'By the end of Silver · cumulative',
    column: 1,
    total: '15',
  },
  GOLDEN: {
    headline: 'Golden — north & south',
    blurb:
      'Irbid and Aqaba showrooms with service centers complete national coverage, the mobile unit and parts sub-dealers filling the gaps between.',
    countsLabel: 'By the end of Golden · cumulative',
    column: 2,
    total: '24',
  },
};

export const PRESENCE_NOTE =
  'Counts are cumulative — each age keeps what the last one built. Select an age to filter the map and read that age’s build-out; traders and multi-brand service partners extend reach without premises, which is how Silver doubles coverage on a flat cost base.';
